import { Component, EventEmitter, Input, Output, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SmsService } from '../../../services/sms.service';

export interface OtpVerificationState {
    otp: string;
    isLoading: boolean;
    isSendingOtp: boolean;
    isResending: boolean;
    errorMessage: string;
    successMessage: string;
    otpSent: boolean;
    resendCountdown: number;
}

@Component({
    selector: 'app-otp-verification',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './otp-verification.component.html',
    styleUrl: './otp-verification.component.css'
})
export class OtpVerificationComponent implements OnDestroy {
    @Input() phoneNumber: string = '';
    @Input() autoSend: boolean = true;
    @Output() verified = new EventEmitter<void>();
    @Output() error = new EventEmitter<string>();

    state: OtpVerificationState = {
        otp: '',
        isLoading: false,
        isSendingOtp: false,
        isResending: false,
        errorMessage: '',
        successMessage: '',
        otpSent: false,
        resendCountdown: 0
    };

    private countdownInterval: any;

    constructor(private smsService: SmsService) { }

    ngOnInit(): void {
        if (this.autoSend && this.phoneNumber) {
            this.sendOtp();
        }
    }

    ngOnDestroy(): void {
        this.clearCountdown();
    }

    /**
     * Send OTP to the provided phone number
     */
    sendOtp(): void {
        if (!this.phoneNumber) {
            this.state.errorMessage = 'Phone number is required';
            this.error.emit('Phone number is required');
            return;
        }

        const mobile = this.extractMobileNumber(this.phoneNumber);
        if (!this.isValidMobileNumber(mobile)) {
            this.state.errorMessage = 'Invalid phone number format. Must be 10 digits.';
            this.error.emit('Invalid phone number format');
            return;
        }

        this.state.isSendingOtp = true;
        this.state.errorMessage = '';
        this.state.successMessage = '';

        this.smsService.sendOtp({ mobile }).subscribe({
            next: () => {
                this.state.isSendingOtp = false;
                this.state.otpSent = true;
                this.state.successMessage = 'OTP sent to customer';
                this.startResendCountdown(30);
            },
            error: (error) => {
                this.state.isSendingOtp = false;
                this.state.errorMessage = error.error?.message || 'Failed to send OTP';
                this.error.emit(this.state.errorMessage);
            }
        });
    }

    /**
     * Verify the OTP entered by user
     */
    verifyOtp(): void {
        if (!this.state.otp || this.state.otp.length < 4) {
            this.state.errorMessage = 'Please enter a valid OTP (4-8 digits)';
            return;
        }

        const mobile = this.extractMobileNumber(this.phoneNumber);
        if (!this.isValidMobileNumber(mobile)) {
            this.state.errorMessage = 'Invalid phone number format';
            return;
        }

        this.state.isLoading = true;
        this.state.errorMessage = '';
        this.state.successMessage = '';

        this.smsService.verifyOtp({ mobile, otp: this.state.otp }).subscribe({
            next: (response) => {
                this.state.isLoading = false;
                // If we get a response in the success callback, treat it as successful
                // unless there's an explicit error indicator
                if (response.type === 'error' || response.type === 'failure') {
                    this.state.errorMessage = response.message || 'Verification failed';
                    this.error.emit(this.state.errorMessage);
                } else {
                    this.state.successMessage = 'OTP Verified!';
                    this.verified.emit();
                }
            },
            error: (error) => {
                this.state.isLoading = false;
                this.state.errorMessage = error.error?.message || 'Invalid OTP';
                this.error.emit(this.state.errorMessage);
            }
        });
    }

    /**
     * Resend OTP
     */
    resendOtp(): void {
        if (this.state.resendCountdown > 0) return;

        const mobile = this.extractMobileNumber(this.phoneNumber);
        if (!this.isValidMobileNumber(mobile)) {
            this.state.errorMessage = 'Invalid phone number format';
            return;
        }

        this.state.isResending = true;
        this.state.errorMessage = '';
        this.state.successMessage = '';

        this.smsService.resendOtp({ mobile }).subscribe({
            next: () => {
                this.state.isResending = false;
                this.state.successMessage = 'OTP resent successfully';
                this.state.otp = '';
                this.startResendCountdown(30);
            },
            error: (error) => {
                this.state.isResending = false;
                this.state.errorMessage = error.error?.message || 'Failed to resend OTP';
                this.error.emit(this.state.errorMessage);
            }
        });
    }

    /**
     * Reset the OTP verification state
     */
    reset(): void {
        this.clearCountdown();
        this.state = {
            otp: '',
            isLoading: false,
            isSendingOtp: false,
            isResending: false,
            errorMessage: '',
            successMessage: '',
            otpSent: false,
            resendCountdown: 0
        };
    }

    /**
     * Handle OTP input - only allow digits
     */
    onOtpInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        input.value = input.value.replace(/\D/g, '');
        this.state.otp = input.value;
    }

    /**
     * Extract 10-digit mobile number from phone string
     */
    private extractMobileNumber(phoneNumber: string): string {
        const digits = phoneNumber.replace(/\D/g, '');
        return digits.slice(-10);
    }

    /**
     * Validate mobile number format (10 digits)
     */
    private isValidMobileNumber(mobile: string): boolean {
        return /^\d{10}$/.test(mobile);
    }

    /**
     * Start countdown timer for resend button
     */
    private startResendCountdown(seconds: number): void {
        this.clearCountdown();
        this.state.resendCountdown = seconds;
        this.countdownInterval = setInterval(() => {
            this.state.resendCountdown--;
            if (this.state.resendCountdown <= 0) {
                this.clearCountdown();
            }
        }, 1000);
    }

    /**
     * Clear countdown interval
     */
    private clearCountdown(): void {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }
        this.state.resendCountdown = 0;
    }
}
